import React, { FC, useEffect } from 'react'
import { Font, Heading2 } from 'is24-corecss'
import { useDispatch, useSelector } from 'react-redux'
import { FontsProps } from 'is24-corecss/build/@types/Fonts/FontsProps'
import { StepTemplate } from '../../components/styled/stepTemplate/StepTemplate'
import './ResponseNonHappyPathStep.scss'
import { useReportViewEvent } from '../../hooks'
import { IntegrationType } from '../../@types/config'
import { ReduxState } from '../../store/reducers'
import ValuationBar from '../../components/ValuationBar/ValuationBar'
import { Button } from '../../components/styled/CosmaButton/CosmaButton'
import { reportTrackingEvent } from '../../tracking/thunks'
import { Intent } from '../../@types/intent'
import { richValuationFlowProgress } from '../../progress/valuationProgressHelper'
import { STEP } from '../../@types/state'
import { content } from './content'
import { redirect } from './redirect'
import { RealEstateType } from '../../@types/realEstateType'
import { updateNonHappyPath } from '../../store/flow/flowSlice'
import { StatusMessage } from '../../components/styled/StatusMessage/StatusMessage'
import { FeatureName, getFeatureToggleValue } from '../../featureToggles/featureToggles'

import { redirectWithToggle } from './redirectWithToggle'
import { enrichLead } from '../../store/lead/thunks'

const tinyColor = require('tinycolor2')

export interface ResponseStepProps {
  goToNextStep: () => unknown
  goToErrorStep: () => void
}
export const ResponseNonHappyPathStep: FC<ResponseStepProps> = (props) => {
  useReportViewEvent({ pageTitle: 'response' })
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(updateNonHappyPath(true))
  }, [])

  const { lead, config } = useSelector((state: ReduxState) => state)
  const { theme, integrationType, illustration } = config
  const { id: leadId, intent, realEstateType, commercialRealEstateType } = lead
  const valuationBarProgress = richValuationFlowProgress(STEP.ADDRESS)
  const isIv24Integration = integrationType === IntegrationType.IMMOVERKAUF24
  const isRemoveContactScreenEnabled = getFeatureToggleValue(FeatureName.REMOVE_CONTACT_SCREEN)
  const isLand = realEstateType == RealEstateType.LAND
  const isCommercial = realEstateType == RealEstateType.COMMERCIAL

  useEffect(() => {
    if (
      leadId &&
      ((isCommercial &&
        (intent == Intent.MAKLER_SALE || intent == Intent.LET || intent == Intent.RENT || intent == Intent.BUY)) ||
        (isLand &&
          (intent == Intent.INDEPENDENT_SALE || intent == Intent.LET || intent == Intent.RENT || intent == Intent.BUY)))
    ) {
      dispatch(
        enrichLead({
          pageTitle: `${isRemoveContactScreenEnabled ? 'response' : 'ppa-promo'}`,
          request: {
            id: leadId,
            flowComplete: true,
          },
        })
      )
    }
  }, [leadId])

  if (!intent) {
    // redirect to error page, once there is one
    return <></>
  }

  const headline = isCommercial ? '' : isLand ? 'Dein ungefährer Grundstückswert' : `Dein ungefährer Immobilienwert`
  const adjustedIntent = isCommercial && intent == Intent.MAKLER_SALE ? Intent.INDEPENDENT_SALE : intent

  const goToNextStep = () => {
    dispatch(
      reportTrackingEvent({
        pageTitle: `${isRemoveContactScreenEnabled ? 'response' : 'ppa-promo'}`,
        label: 'rle-promo_skip',
        action: 'realtor-leadengine',
        category: 'residential',
        eventName: 'lead_input',
        inputValue: 'skip_promotion',
      })
    )

    props.goToNextStep()
  }

  const handleSubmit = () => {
    dispatch(
      reportTrackingEvent({
        pageTitle: `${isRemoveContactScreenEnabled ? 'response' : 'ppa-promo'}`,
        label: 'rle-promo_submit',
        action: 'realtor-leadengine',
        category: 'residential',
        eventName: 'promotion_click',
        inputValue: '',
      })
    )
    isRemoveContactScreenEnabled
      ? redirectWithToggle(adjustedIntent, realEstateType)
      : redirect(adjustedIntent, realEstateType, commercialRealEstateType)
  }
  const consultationStatusMessage =
    'Wir werden uns zeitnah bei dir melden, um eine unverbindliche Beratung durchzuführen.'
  const statusMessageContent = lead.isConsultationAccepted
    ? consultationStatusMessage
    : content.get(adjustedIntent)?.statusContent

  const statusMessage = (
    <div
      className={`status-message-container margin-bottom-xl padding-horizontal-l ${
        isIv24Integration ? 'iv24StatusMessageScopedCssFix' : ''
      }`}
      data-testid='status-headline'
    >
      <StatusMessage
        headline={content.get(adjustedIntent)?.statusHeadline ?? ''}
        statusType='confirm'
        statusStyle={'toast'}
        message={statusMessageContent}
        textColor={theme.text}
        borderColor={theme.primary}
        backgroundColor={theme.background}
      />
    </div>
  )

  const subHeadline =
    isIv24Integration && adjustedIntent === Intent.INDEPENDENT_SALE
      ? 'Jetzt bei ImmoScout24 inserieren und attraktiven Preis erzielen'
      : content.get(adjustedIntent)?.subHeadline

  const ctaButton =
    isIv24Integration && adjustedIntent === Intent.INDEPENDENT_SALE
      ? 'Bei ImmoScout24 inserieren'
      : content.get(adjustedIntent)?.ctaButton

  return (
    <StepTemplate
      headline={headline}
      displayNext={false}
      header={statusMessage}
      className={'non-happy-path-response-step'}
      backgroundColor={theme.background}
      textColor={theme.text}
      primary={theme.primary}
      illus={illustration}
    >
      <div className='grid-flex grid-justify-center' data-testing='response-container'>
        {!isCommercial && (
          <div className={'valuation'}>
            <ValuationBar progress={valuationBarProgress} valuationAmountClass={` font-m`} largeValuationBar={true} />
          </div>
        )}
        <div data-testid='response-sub-headline' className='padding-top-xl grid-item one-whole align-center'>
          <Heading2>{subHeadline}</Heading2>
        </div>
        <div className='grid-item desk-one-half lap-two-thirds palm-one-whole justify-center details-holder'>
          <ul
            data-testid='bullet-points'
            className={`list-check status-list horizontal-center ${content.get(adjustedIntent)?.additionalUlClass}`}
          >
            <li data-testid='bullet-point-1'>{content.get(adjustedIntent)?.bulletPoint1}</li>
            <li data-testid='bullet-point-2'>{content.get(adjustedIntent)?.bulletPoint2}</li>
            <li data-testid='bullet-point-3'>{content.get(adjustedIntent)?.bulletPoint3}</li>
          </ul>
        </div>
        <div className='grid-item one-whole desk-margin-top-m lap-margin-top-m palm-margin-top-xl padding-horizontal-l align-center'>
          <Button
            data-testid='response-step-unhappy-path-cta'
            onClick={handleSubmit}
            appearance='primary'
            className='palm-one-whole status-btn'
            primary={theme.primary}
            secondary={theme.secondary}
            textColor={
              integrationType === IntegrationType.EXTERNAL && tinyColor(theme?.primary).isDark()
                ? '#FFFFFF'
                : theme.text
            }
            secondaryLight={theme.secondaryLight}
          >
            {ctaButton}
          </Button>
        </div>
        {!isCommercial && !isLand && !isIv24Integration && (
          <div className='grid-item one-whole margin-top-m padding-horizontal-l align-center'>
            <a
              href='#/'
              onClick={() => goToNextStep()}
              style={{ color: isIv24Integration ? '#2780ba' : '#2267e8' }}
              data-testid='non-happy-response-step-text-link'
            >
              Analyse erhalten
            </a>
          </div>
        )}
        {adjustedIntent == Intent.INDEPENDENT_SALE && (
          <div className='legal-note-container grid grid-flex grid-justify-center'>
            <div
              data-testid='non-happy-response-legal-note'
              className='grid-item one-whole align-center non-happy-response-legal-note padding-horizontal-m'
            >
              <Font color={theme.text ? (theme.text as FontsProps['color']) : 'lightgray'}>
                ¹ Similar Web: Monatliche Besuche Nov. 2020-Nov.2022, Google Analytics: Website-Besuche April 2021.{' '}
                <br />
                ² 2022, im Vergleich von Median-Werten zu Immowelt und eBay Kleinanzeigen für Wohnungen und Häuser in
                Deutschland, definiert durch Immobilie <br />+ Bundesland + Region + Immobilientyp + Größe <br />³
                basierend auf eKomi-Bewertung Stand: 7/2023
              </Font>
            </div>
          </div>
        )}
        {adjustedIntent == Intent.LET && (
          <div className='legal-note-container grid grid-flex grid-justify-center'>
            <div
              data-testid='non-happy-response-legal-note'
              className='grid-item one-whole align-center non-happy-response-legal-note padding-horizontal-m'
            >
              <Font color={theme.text ? (theme.text as FontsProps['color']) : 'lightgray'}>
                ¹ Private Anbieter können Wohnimmobilien kostenlos als Basisanzeige inserieren. Das Inserat hat eine
                Laufzeit von 14 Tagen bei Vermietung oder 7 <br className='lap-hide palm-hide' /> Tagen bei
                Nachmieter-Suche. Sie erhalten bis zu 10 Kontaktanfragen. Die Anzeige endet automatisch. Es ist maximal
                eine kostenlose Anzeige pro <br className='lap-hide palm-hide' /> 6 Monate buchbar.
                <br />² 2022, im Vergleich von Median-Werten zu Immowelt und eBay Kleinanzeigen für Wohnungen und Häuser
                in Deutschland, definiert durch Immobilie <br className='lap-hide palm-hide' /> + Bundesland + Region +
                Immobilientyp + Größe <br />³ basierend auf eKomi-Bewertung Stand: 7/2023
              </Font>
            </div>
          </div>
        )}
      </div>
    </StepTemplate>
  )
}

export default ResponseNonHappyPathStep
